import type { PastVisitInput, PastVisitPreview, PastVisitResult } from "@vaulted/shared";
import { BOULEVARD_VISIT_COLUMNS, formatCents } from "@vaulted/shared";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, post } from "../api";
import { Alert, Button, PageHeader, Panel, PanelHeader } from "../components/ui";
import { excelDate, readSpreadsheet } from "../lib/spreadsheet";
import { dayLabel } from "./Calendar";
import { BackLink } from "../components/BackLink";

/** "03:30 PM" → "15:30". */
function time24(t: string): string | null {
  const m = /^(\d{1,2}):(\d{2})\s*([AP]M)?$/i.exec(t.trim());
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (m[3]?.toUpperCase() === "PM") h += 12;
  else if (!m[3]) h = Number(m[1]);
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}
const cents = (v: string) => (v.trim() && Number.isFinite(Number(v)) ? Math.round(Number(v) * 100) : 0);

/**
 * Boulevard's "Appointment line items" report as visits: each appointment has a "Total" row and
 * one row per service and add-on.
 */
export function toVisits(sheet: string[][]): PastVisitInput[] {
  const [head, ...data] = sheet;
  if (!head) throw new Error("The file is empty.");
  const C = BOULEVARD_VISIT_COLUMNS;
  const ix = Object.fromEntries(Object.entries(C).map(([k, name]) => [k, head.findIndex((h) => h.trim().toLowerCase() === name.toLowerCase())])) as Record<keyof typeof C, number>;
  for (const k of ["ref", "date", "itemRef"] as const) if (ix[k] < 0) throw new Error(`This doesn't look like Boulevard's Appointment line items report: there's no "${C[k]}" column.`);
  const get = (r: string[], k: keyof typeof C) => (ix[k] >= 0 ? (r[ix[k]] ?? "").trim() : "");
  const visits = new Map<string, PastVisitInput & { discounts: Set<string>; hasTotal: boolean }>();
  for (const r of data) {
    const ref = get(r, "ref");
    // The report's grand total row has no client.
    if (!ref || (!get(r, "clientRef") && !get(r, "clientName"))) continue;
    const date = excelDate(get(r, "date"));
    if (!date) continue;
    const v =
      visits.get(ref) ??
      visits
        .set(ref, {
          ref,
          clientRef: get(r, "clientRef") || null,
          clientName: get(r, "clientName"),
          email: get(r, "email") || null,
          mobile: get(r, "mobile") || null,
          date,
          start: null,
          end: null,
          nurse: get(r, "nurse"),
          status: get(r, "state"),
          discount: "",
          totalCents: 0,
          items: [],
          discounts: new Set(),
          hasTotal: false,
        })
        .get(ref)!;
    for (const d of get(r, "discount").split(",")) if (d.trim()) v.discounts.add(d.trim());
    if (get(r, "itemRef") === "Total") {
      v.hasTotal = true;
      v.totalCents = cents(get(r, "amount"));
      v.start = time24(get(r, "start")) ?? v.start;
      v.end = time24(get(r, "end")) ?? v.end;
      v.status = get(r, "state") || v.status;
      continue;
    }
    if (!get(r, "itemName")) continue;
    v.items.push({ name: get(r, "itemName"), category: get(r, "category"), addOn: get(r, "addOn").toUpperCase() === "TRUE", amountCents: cents(get(r, "amount")) });
    const start = time24(get(r, "start"));
    if (start && (!v.start || start < v.start)) v.start = start;
    const end = time24(get(r, "end"));
    if (end && (!v.end || end > v.end)) v.end = end;
  }
  return [...visits.values()].map(({ discounts, hasTotal, ...v }) => ({
    ...v,
    discount: [...discounts].join(", "),
    totalCents: hasTotal ? v.totalCents : v.items.reduce((n, i) => n + i.amountCents, 0),
    // Services first, then add-ons, as on the visit.
    items: [...v.items.filter((i) => !i.addOn), ...v.items.filter((i) => i.addOn)],
  }));
}

/** Clients → Import → Past visits: Boulevard's appointment history as read-only visits on each client. */
export function PastVisitImportPage() {
  const [file, setFile] = useState<string | null>(null);
  const [visits, setVisits] = useState<PastVisitInput[] | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const preview = useMutation({ mutationFn: (v: PastVisitInput[]) => post<PastVisitPreview>("/api/import/past-visits/preview", { visits: v }) });
  const run = useMutation({ mutationFn: () => post<PastVisitResult>("/api/import/past-visits", { visits }) });
  const p = preview.data;

  const pickFile = async (f: File | undefined) => {
    if (!f) return;
    setReadError(null);
    preview.reset();
    run.reset();
    try {
      const v = toVisits(await readSpreadsheet(f));
      if (!v.length) throw new Error("No visits found in this file.");
      setFile(f.name);
      setVisits(v);
      preview.mutate(v);
    } catch (e) {
      setVisits(null);
      setReadError(errorMessage(e));
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="Import past visits" description="From Boulevard's Appointment line items report. They show on each client's Visits tab as history from before Vaulted." />
      <div className="mb-4">
        <BackLink to="/clients" label="Clients" className="text-sm font-semibold text-brand" />
      </div>
      {run.data ? (
        <Alert tone="ok">
          Done: {run.data.created} visits added, {run.data.updated} updated{run.data.unmatched ? `, ${run.data.unmatched} not matched to a client (not imported)` : ""}.
        </Alert>
      ) : (
        <div className="flex flex-col gap-5">
          <Panel>
            <div className="flex flex-wrap items-center gap-3 px-5 py-5">
              <label className="cursor-pointer rounded-[var(--radius-control)] bg-brand px-4 py-2 font-semibold text-white hover:bg-brand-deep">
                {file ? "Choose a different file" : "Choose file (.xlsx or .csv)"}
                <input type="file" accept=".xlsx,.csv" className="sr-only" onChange={(e) => void pickFile(e.target.files?.[0])} />
              </label>
              {file && <span className="text-sm text-ink-soft">{file}</span>}
            </div>
            <p className="px-5 pb-5 text-sm text-muted">
              Import the clients first. Visits are matched by Boulevard's client ID, then email, then mobile. They're history only: not on the calendar, and not in charts, payouts, or reports. Running it again updates the same visits.
            </p>
          </Panel>
          {readError && <Alert>{readError}</Alert>}
          {preview.error && <Alert>{errorMessage(preview.error)}</Alert>}
          {preview.isPending && <p className="text-sm text-muted">Checking {visits?.length} visits…</p>}
          {p && (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                <Stat label="New visits" value={String(p.create)} />
                <Stat label="Already imported (update)" value={String(p.update)} />
                <Stat label="Services and add-ons" value={String(p.items)} />
                <Stat label="Total paid" value={formatCents(p.totalCents)} />
              </div>
              <p className="text-sm text-ink-soft">
                Matched by Boulevard ID: {p.matchedBy.ref}, by email: {p.matchedBy.email}, by mobile: {p.matchedBy.mobile}.
              </p>
              {p.notFinal.length > 0 && (
                <Panel>
                  <PanelHeader title={`Not closed out in Boulevard (${p.notFinal.length})`} description="They come in labelled that way. Close them out in Boulevard and import a fresh report to update them." />
                  <ul className="divide-y divide-mist">
                    {p.notFinal.map((v, i) => (
                      <li key={i} className="px-5 py-2.5 text-sm">
                        <span className="font-semibold">{dayLabel(v.date, { month: "short", day: "numeric", year: "numeric" })}</span> · {v.status} · {v.items}
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}
              {p.unmatched.length > 0 && (
                <Panel>
                  <PanelHeader title={`No client to put them on (${p.unmatched.length})`} description="These aren't imported. Import or fix the client, then run this again." />
                  <ul className="divide-y divide-mist">
                    {p.unmatched.map((v, i) => (
                      <li key={i} className="px-5 py-2.5 text-sm">
                        <span className="font-semibold">{v.name || "(no name)"}</span> · {dayLabel(v.date, { month: "short", day: "numeric", year: "numeric" })} · <span className="text-muted">{v.reason}</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}
              {run.error && <Alert>{errorMessage(run.error)}</Alert>}
              <div>
                <Button variant="primary" busy={run.isPending} disabled={!p.create && !p.update} onClick={() => run.mutate()}>
                  Import {p.create + p.update} visits
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Panel className="p-4">
      <p className="text-sm text-muted">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
    </Panel>
  );
}
